import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Boxes,
  CalendarClock,
  CheckCircle2,
  PauseCircle,
  PackageX,
  RotateCcw,
  Star,
  Undo2,
  Wallet,
} from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import {
  useStoreAnalyticsQuery,
  useStorePayoutConfigQuery,
  useStorePlanQuery,
} from '@/client/api/stores/stores.query';
import {
  useStoreAnalyticsSeriesQuery,
  useStoreLowStockQuery,
  useStoreRefundRequestsQuery,
  useStoreReturnRequestsQuery,
} from '@/client/api/stores/parity.query';
import { cn } from '@/lib/utils';

import { InfoRow, RangePicker, SectionCard, StoreOwnerLink, bucketFor, money, rangeFor, shortDate, useRangeParam } from './shared';
import { SalesChart } from './SalesChart';

const on = (v: unknown) => v === 1 || v === true;
const DAY = 24 * 3600 * 1000;

type Tone = 'critical' | 'warning' | 'info';

interface Attention {
  key: string;
  icon: React.ReactNode;
  title: string;
  detail?: string;
  tone: Tone;
  tab?: string;
}

const TONE: Record<Tone, string> = {
  critical: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  info: 'border-gray-200 bg-gray-50 text-gray-800',
};

const AttentionItem: React.FC<{ item: Attention; onOpen?: (tab: string) => void }> = ({ item, onOpen }) => {
  const clickable = item.tab && onOpen;
  const body = (
    <>
      <span className="mt-0.5 flex-shrink-0">{item.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{item.title}</span>
        {item.detail && <span className="block truncate text-xs opacity-80">{item.detail}</span>}
      </span>
      {clickable && <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-60" />}
    </>
  );
  return clickable ? (
    <button type="button" onClick={() => onOpen!(item.tab!)} className={cn('flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-opacity hover:opacity-90', TONE[item.tone])}>
      {body}
    </button>
  ) : (
    <div className={cn('flex items-start gap-3 rounded-lg border px-3 py-2.5', TONE[item.tone])}>{body}</div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</p>
  </div>
);

/**
 * The store at a glance: sales over the selected range (90 days by default,
 * kept in `?range=`), what needs a staff member's attention right now, lifetime
 * totals, and who owns it on which plan.
 */
export const OverviewTab: React.FC<{ store: AdminStore; onNavigate?: (tab: string) => void }> = ({ store, onNavigate }) => {
  const [range, setRange] = useRangeParam('90d');
  const dates = rangeFor(range);
  const { data: series, isLoading } = useStoreAnalyticsSeriesQuery(store.storeid, { ...dates, bucket: bucketFor(range) });
  const { data: lifetime } = useStoreAnalyticsQuery(store.storeid);
  const { data: lowStock } = useStoreLowStockQuery(store.storeid);
  const { data: refunds } = useStoreRefundRequestsQuery(store.storeid, { status: 'pending', limit: 1 });
  const { data: returns } = useStoreReturnRequestsQuery(store.storeid, { status: 'pending', limit: 1 });
  const { data: payoutConfig } = useStorePayoutConfigQuery(store.storeid);
  const { data: planData } = useStorePlanQuery(store.storeid);

  const plan = planData?.plan ?? null;
  const expires = plan?.date_plan_expires ? new Date(plan.date_plan_expires) : null;
  const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / DAY) : null;

  const attention: Attention[] = [];
  if (on(store.banned)) attention.push({ key: 'ban', icon: <AlertTriangle className="h-4 w-4" />, title: 'Store is banned', detail: store.ban_reason ?? undefined, tone: 'critical', tab: 'Moderation' });
  else if (on(store.admin_closed)) attention.push({ key: 'closed', icon: <AlertTriangle className="h-4 w-4" />, title: 'Force-closed by staff', detail: store.admin_close_reason ?? undefined, tone: 'critical', tab: 'Moderation' });
  if (on(store.suspended)) attention.push({ key: 'suspended', icon: <PauseCircle className="h-4 w-4" />, title: 'Suspended', detail: store.suspend_reason ?? undefined, tone: 'critical', tab: 'Moderation' });
  if (on(store.review)) attention.push({ key: 'review', icon: <AlertTriangle className="h-4 w-4" />, title: 'On review hold', detail: store.review_reason ?? undefined, tone: 'warning', tab: 'Moderation' });
  if ((refunds?.total_count ?? 0) > 0) attention.push({ key: 'refunds', icon: <RotateCcw className="h-4 w-4" />, title: `${refunds!.total_count} refund request${refunds!.total_count === 1 ? '' : 's'} waiting`, tone: 'warning', tab: 'Refunds' });
  if ((returns?.total_count ?? 0) > 0) attention.push({ key: 'returns', icon: <Undo2 className="h-4 w-4" />, title: `${returns!.total_count} return request${returns!.total_count === 1 ? '' : 's'} waiting`, tone: 'warning', tab: 'Refunds' });
  if (on(payoutConfig?.payout_hold)) attention.push({ key: 'payout', icon: <Wallet className="h-4 w-4" />, title: 'Payouts on hold', detail: payoutConfig?.payout_hold_reason ?? undefined, tone: 'warning', tab: 'Payout' });
  if (!on(store.plan_exempt) && daysLeft != null && daysLeft <= 14)
    attention.push({
      key: 'plan', icon: <CalendarClock className="h-4 w-4" />,
      title: daysLeft < 0 ? 'Plan has expired' : `Plan expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
      detail: expires ? shortDate(expires.toISOString()) : undefined,
      tone: daysLeft < 0 ? 'critical' : 'warning', tab: 'Billing',
    });
  if ((lowStock?.total_count ?? 0) > 0) attention.push({ key: 'stock', icon: <PackageX className="h-4 w-4" />, title: `${lowStock!.total_count} listing${lowStock!.total_count === 1 ? '' : 's'} low on stock`, detail: lowStock!.inventory.slice(0, 3).map((l) => l.entity_name).filter(Boolean).join(', '), tone: 'info', tab: 'Inventory' });

  return (
    <div className="space-y-4">
      <SalesChart
        series={series}
        loading={isLoading}
        description={series ? `Per ${series.bucket}, compared with the previous ${range.replace('d', ' days').replace('365 days', '12 months')}` : undefined}
        actions={<RangePicker value={range} onChange={setRange} />}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Needs attention" description={attention.length ? undefined : 'Nothing waiting on staff.'}>
          {attention.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> All clear
            </p>
          ) : (
            <div className="space-y-2">
              {attention.map((a) => <AttentionItem key={a.key} item={a} onOpen={onNavigate} />)}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Lifetime">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Order GMV" value={money(lifetime?.orders.gmv)} />
            <Stat label="Orders" value={lifetime?.orders.count.toLocaleString() ?? '—'} />
            <Stat label="Active listings" value={lifetime?.active_listings.toLocaleString() ?? '—'} />
            <Stat label="Paid out (net)" value={money(lifetime?.payouts.net_amount)} />
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
            <Boxes className="h-3.5 w-3.5" /> Since {shortDate(store.date_added ?? null)}
          </p>
        </SectionCard>

        <SectionCard title="Store">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {on(store.verified) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"><BadgeCheck className="h-3 w-3" /> Verified</span>
            )}
            {on(store.featured) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700"><Star className="h-3 w-3" /> Featured</span>
            )}
            {on(store.plan_exempt) && (
              <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">Plan exempt</span>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            <InfoRow label="Owner">
              <StoreOwnerLink store={store} />
            </InfoRow>
            <InfoRow label="Plan">
              {on(store.plan_exempt) ? 'Exempt' : plan?.plan_name ?? plan?.plan_tier ?? (plan?.is_free ? 'Free' : '—')}
            </InfoRow>
            <InfoRow label="Renews / expires">{expires ? shortDate(expires.toISOString()) : '—'}</InfoRow>
            <InfoRow label="Commission">
              {store.commission_rate_override != null
                ? `${store.commission_rate_override}% (override)`
                : planData?.entitlements?.commission_rate != null ? `${planData.entitlements.commission_rate}%` : '—'}
            </InfoRow>
            <InfoRow label="Handle">/{store.slug ?? '—'}</InfoRow>
            <InfoRow label="Country">{store.country_iso2 ?? '—'}</InfoRow>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};
